import React, { useContext } from 'react';
import { EntitySelector, GroupBy } from '@nile/reports/src/modules/component';
import { GET_REPORT_MODULES } from '@nile/reports/src/modules/DataReports/graphql';
import { GET_ALERT_META_INFO_V4 } from '@nile/rmm/src/modules/alert-management/Graphql/alert';
import { GET_ASSET_VIEW_META_INFO } from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import {
  GET_PATCH_DETAILS_LISTVIEW_META,
  GET_CONDITIONS_META_FOR_RMM_MODULE_REPORTS,
  GET_SOFTWARELIST_META
} from '@nile/rmm/src/modules/patch-management/Graphql/patch';
import { GET_CONDITIONS_META_FOR_INVOICE } from '@nile/graphql/src/Query/client_management/invoice';
import {
  GET_CONDITIONS_META_FOR_OFFERED_ITEM,
  GET_CONDITIONS_META_FOR_WORKLOG_ITEM
} from '@nile/graphql/src/Query/client_management/client';
import {
  ReportQueryBuilderForm,
  Loader,
  useProduct,
  COMMON_CONSTANTS,
  withErrorBoundary
} from '@nile/components';
import { MODULEMAP } from '@nile/reports/src/modules/charts/constants';
import {
  AccountClient,
  useCachedQuery,
  GET_CLIENTUSER_ASSOCIATION_LISTVIEW_METAINFO,
  GET_CUSTOMER_FEEDBACK_RESPONSE_META,
  GET_CLIENT_LIST_META_V2,
  GET_RULEBUILDER_OPERATORS,
  RMMClient,
  GET_TICKET_LISTVIEW_META,
  TicketClient
} from '@nile/graphql';
import DataReportsContext from '@nile/reports/src/modules/DataReports/contexts';
import { TextStyle } from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import ColumnSelector from '@nile/reports/src/modules/DataReports/components/subpages/DataReportConfig/ColumnSelector';
import { isArray } from 'lodash';
import { useQuery } from '@apollo/client';

function DataReportConfig(props) {
  const {
    data,
    meta,
    metaLoading,
    updateData,
    allEntities = [],
    errors = null
  } = props;

  const intl = useIntl();
  const dataContext = useContext(DataReportsContext);
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);

  const { loading: moduleLoading, data: reportModules } = useCachedQuery(
    GET_REPORT_MODULES,
    {
      client: AccountClient
    }
  );

  const { loading: operatorsLoading, data: allOperators } = useCachedQuery(
    GET_RULEBUILDER_OPERATORS
  );

  const {
    loading: ticketListViewMetaLoading,
    data: ticketListViewMeta
  } = useQuery(GET_TICKET_LISTVIEW_META, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const { loading: assetViewMetaLoading, data: assetViewMeta } = useQuery(
    GET_ASSET_VIEW_META_INFO,
    {
      client: RMMClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: patchMetaLoading, data: patchMeta } = useQuery(
    GET_PATCH_DETAILS_LISTVIEW_META,
    {
      client: RMMClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: rmmPatchLoading, data: rmmPatchMeta } = useQuery(
    GET_CONDITIONS_META_FOR_RMM_MODULE_REPORTS,
    {
      client: RMMClient,
      variables: { rmmModule: data?.entity },
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: rmmSoftwareMetaLoading, data: rmmSoftwareMeta } = useQuery(
    GET_SOFTWARELIST_META,
    {
      client: RMMClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: alertMetaLoading, data: alertMeta } = useQuery(
    GET_ALERT_META_INFO_V4,
    {
      client: RMMClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: invoiceMetaLoading, data: invoiceMeta } = useQuery(
    GET_CONDITIONS_META_FOR_INVOICE,
    {
      client: AccountClient,
      skip: !isMSPProduct,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );
  const { loading: offeredItemMetaLoading, data: offeredItemMeta } = useQuery(
    GET_CONDITIONS_META_FOR_OFFERED_ITEM,
    {
      client: AccountClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const { loading: worklogMetaLoading, data: worklogMeta } = useQuery(
    GET_CONDITIONS_META_FOR_WORKLOG_ITEM,
    {
      client: AccountClient,
      fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
    }
  );

  const {
    loading: clientListViewMetaLoading,
    data: clientListViewMeta
  } = useQuery(GET_CLIENT_LIST_META_V2, {
    client: AccountClient,
    skip: !isMSPProduct,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const {
    loading: requesterListMetaLoading,
    data: requesterListMeta
  } = useQuery(GET_CLIENTUSER_ASSOCIATION_LISTVIEW_METAINFO, {
    client: AccountClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const {
    loading: feedbackResponseMetaLoading,
    data: feedbackResponseMeta
  } = useQuery(GET_CUSTOMER_FEEDBACK_RESPONSE_META, {
    client: TicketClient,
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE
  });

  const getModuleMeta = (module) => {
    const modulesMeta = {
      Ticket: ticketListViewMeta.getTicketListViewMetaInfo,
      WorkStation: assetViewMeta.getAssetViewConditionMetaInfo,
      Alert: alertMeta.getAlertViewMetaInfoV4,
      PatchDetail: patchMeta.getPatchDetailListViewMetaInfo,
      PatchHealth: rmmPatchMeta.getConditionsMetaForRmmReports,
      AntiVirusHealth: rmmPatchMeta.getConditionsMetaForRmmReports,
      DataBackupHealth: rmmPatchMeta.getConditionsMetaForRmmReports,
      Invoice: invoiceMeta?.getInvoiceListViewMeta,
      OfferedItem: offeredItemMeta.getOfferedItemListViewMeta,
      AssetSoftwareInventory: rmmSoftwareMeta.getConditionsMetaForRmmReports,
      AssetPatchDetail: rmmPatchMeta.getConditionsMetaForRmmReports,
      Client: clientListViewMeta?.getClientListViewMetaInfoV2,
      RequesterAssociation:
        requesterListMeta.getClientUserAssociationListViewMetaInfo,
      HoursWorklogEntry: worklogMeta.getWorklogEntriesListViewMeta,
      FeedbackSurveyResponse:
        feedbackResponseMeta.getFeedbackSurveyListViewMeta,
      AssetRemoteSession: rmmPatchMeta.getConditionsMetaForRmmReports,
      NMAsset: rmmPatchMeta.getConditionsMetaForRmmReports,
      AllAssets: rmmPatchMeta.getConditionsMetaForRmmReports
    };
    return modulesMeta[module] || null;
  };

  const updateAutomationRule = (conditions, key) => {
    const newCondition = conditions?.operands?.length ? conditions : null;
    updateData('reportData', { ...data, dataCondition: newCondition });
  };

  const updateEntity = (ent) => {
    dataContext.setLoading(true);
    dataContext.updateContext({
      ...dataContext.value,
      entity: ent.name,
      selectAttributes: [],
      dataCondition: null,
      groupBy: null
    });
    setTimeout(() => dataContext.setLoading(false), 1000);
  };

  const ruleBuilderContext = {
    rule: data.dataCondition,
    updateAutomationRule
  };

  const queryLoader =
    moduleLoading ||
    operatorsLoading ||
    assetViewMetaLoading ||
    patchMetaLoading ||
    alertMetaLoading ||
    rmmPatchLoading ||
    invoiceMetaLoading ||
    ticketListViewMetaLoading ||
    rmmSoftwareMetaLoading ||
    metaLoading ||
    clientListViewMetaLoading ||
    requesterListMetaLoading ||
    worklogMetaLoading ||
    feedbackResponseMetaLoading ||
    offeredItemMetaLoading;

  return (
    <div className="fw fh p20">
      <div className="mb40">
        <EntitySelector
          loading={moduleLoading || operatorsLoading}
          data={reportModules?.getDataReportModules || []}
          onChange={updateEntity}
          selectedValue={dataContext?.value?.entity}
        />
      </div>
      {queryLoader ? (
        <div className="fh">
          <Loader />
        </div>
      ) : (
        <div className="configuration">
          <div className="mb40 ml4">
            <ColumnSelector
              data={data}
              updateData={updateData}
              errors={errors}
              meta={meta}
              intl={intl}
              selectedModule={reportModules?.getDataReportModules.filter(
                (el) => el?.name === dataContext?.value?.entity
              )}
            />
          </div>
          <div className="mb40 ml4">
            <div className="mb10 uppercase ">
              <TextStyle weight="Bold">
                <FormattedMessage id="reports.chart.config.filterConditions.header" />
              </TextStyle>
            </div>
            {queryLoader ? (
              <div className="conditionCard configCard flex-center">
                <Loader />
              </div>
            ) : (
              <div className="reportBuilder">
                <ReportQueryBuilderForm
                  currentModule={MODULEMAP[data.entity]}
                  meta={getModuleMeta(data?.entity)}
                  allEntities={{
                    getEntitiesMeta: [
                      ...(isArray(allEntities)
                        ? [...allEntities]
                        : [allEntities])
                    ]
                  }}
                  allOperators={allOperators}
                  context={ruleBuilderContext}
                  query={ruleBuilderContext.rule}
                  pickEntityFileds={data?.entity === 'RequesterAssociation'}
                  errors={errors?.['dataCondition.operands']}
                />
              </div>
            )}
          </div>
          <div className="mb40 ml4">
            <GroupBy
              intl={intl}
              metaLoading={metaLoading}
              meta={meta}
              errors={errors?.groupBy}
              context={dataContext}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default withErrorBoundary(DataReportConfig);
